import { websiteBuilderRenderRichTextToHTML } from "../richText/rendering/websiteBuilderRichTextRenderer";
import classNames from "classnames";
import { websiteBuilderHeadingRichTextExtensions, websiteBuilderParagraphRichTextExtensions } from "../richText/extensions/websiteBuilderRichTextExtensions";
import { websiteBuilderAddLinkProtocol } from "../utility/websiteBuilderAddLinkProtocol";
import { TFunction } from "i18next";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderWebsiteVersionNavigationMenuPageItem, TWebsiteBuilderWebsiteVersionPage, IWebsiteBuilderWebsiteVersionNavigationMenuExternalItem, TWebsiteBuilderWebsiteVersionNavigationMenuItem, IWebsiteBuilderIconAsset } from "redux/reducers/models/websiteBuilder";
import { WB_SECTION_HEIGHT_DESKTOP_CSS_VARIABLE, WB_SECTION_HEIGHT_TABLET_CSS_VARIABLE, WB_SECTION_HEIGHT_MOBILE_CSS_VARIABLE, WB_SECTION_PARALLAX_SHIFT_CSS_VARIABLE, WB_SECTION_PARALLAX_TRAVEL_CSS_VARIABLE, websiteBuilderGetSectionEntityCssVariable } from "./shared/cssVariables";
import { TWebsiteBuilderRootCSSVariables } from "./shared/rootCssVariables";
import { TWebsiteBuilderDevice } from "./shared/device";
import { IFormBuilderForm } from "../formBuilder/editor/model/formBuilderForm";
import { isWebsiteBuilderStretchContainerData, WEBSITE_BUILDER_CONTAINER_CLASS_NAME } from "../nodes/container/websiteBuilderContainer";
import { websiteBuilderGenerateFlexDesignStyles } from "../nodes/container/websiteBuilderFlex";
import { websiteBuilderGenerateGridDesignStyles } from "../nodes/container/websiteBuilderGrid";
import { TWebsiteBuilderSlideshowDataset, websiteBuilderGenerateSlideshowDesignStyles } from "../nodes/container/websiteBuilderSlideshow";
import { websiteBuilderGenerateTableCellDesignStyles, websiteBuilderGenerateTableDesignStyles, websiteBuilderGenerateTableRowDesignStyles } from "../nodes/container/websiteBuilderTable";
import { websiteBuilderGenerateButtonDesignStyles, TWebsiteBuilderButtonDataset } from "../nodes/element/websiteBuilderButton";
import { websiteBuilderGenerateDividerDesignStyles } from "../nodes/element/websiteBuilderDivider";
import { isWebsiteBuilderStretchElementData, websiteBuilderElementFilled, websiteBuilderGenerateElementDesignStyles } from "../nodes/element/websiteBuilderElement";
import { websiteBuilderGenerateImageDesignStyles } from "../nodes/element/websiteBuilderImage";
import { websiteBuilderGenerateIconDesignStyles, websiteBuilderFindIconAsset } from "../nodes/element/websiteBuilderIcon";
import { TWebsiteBuilderLinkDestination, websiteBuilderGenerateLinkDesignStyles } from "../nodes/element/websiteBuilderLink";
import { websiteBuilderGenerateLogoDesignStyles } from "../nodes/element/websiteBuilderLogo";
import { websiteBuilderGenerateNavigationMenuDesignStyles } from "../nodes/element/websiteBuilderNavigationMenu";
import { IWebsiteBuilderFooter } from "../nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderHeader } from "../nodes/section/websiteBuilderHeader";
import {
  IWebsiteBuilderSectionBackground,
  TWebsiteBuilderSection,
  WEBSITE_BUILDER_SECTION_BACKGROUND_DARKEN_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_BACKGROUND_MEDIA_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_BORDER_COLOR,
  WEBSITE_BUILDER_SECTION_PARALLAX_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_PARALLAX_MEDIA_CLASS_NAME,
  isWebsiteBuilderSectionBackgroundVideo,
  isWebsiteBuilderSectionParallax,
  websiteBuilderGenerateSectionDesignStyles,
  websiteBuilderGenerateSectionParallaxShift,
  websiteBuilderGetSectionVisibility,
} from "../nodes/section/websiteBuilderSection";
import { TWebsiteBuilderNode, isWebsiteBuilderElementFlowParentNode, isWebsiteBuilderElementParentNode } from "../nodes/websiteBuilderNode";
import { TWebsiteBuilderSectionEntity, TWebsiteBuilderSectionEntityLayout, IWebsiteBuilderSectionEntityStretch } from "../nodes/websiteBuilderSectionEntity";
import { websiteBuilderGenerateForm } from "./websiteBuilderGenerateForm";
import { IWebsiteConfig, WINDOW_WEBSITE_CONFIG } from "./scripts/config";
import { TWebsiteBuilderVideoDataset, websiteBuilderGenerateVideoDesignStyles } from "../nodes/element/websiteBuilderVideo";
import { websiteBuilderGenerateListDesignStyles } from "../nodes/element/websiteBuilderList";
import { websiteBuilderGenerateEmbedDesignStyles, websiteBuilderGenerateEmbedIframeSettings } from "../nodes/element/websiteBuilderEmbed";
import { websiteBuilderGenerateGridCellDesignStyles, websiteBuilderGridCellFilled } from "../nodes/gridCell/websiteBuilderGridCell";
import { isWebsiteBuilderBox, websiteBuilderGenerateBoxDesignStyles } from "../nodes/container/websiteBuilderBox";
import { isWebsiteBuilderTabset, websiteBuilderGenerateTabsetDesignStyles, WEBSITE_BUILDER_TABSET_HEADER_HEIGHT } from "../nodes/container/websiteBuilderTabset";
import { websiteBuilderGenerateTabDesignStyles } from "../nodes/tab/websiteBuilderTab";
import { IWebsiteBuilderRoot, WEBSITE_BUILDER_ROOT_CLASS_NAME } from "../nodes/root/websiteBuilderRoot";
import { websiteBuilderGenerateMarqueeIconItemDesignStyles, websiteBuilderGenerateMarqueeDesignStyles, websiteBuilderGenerateMarqueeLinkItemDesignStyles, websiteBuilderGenerateMarqueeParagraphItemDesignStyles } from "../nodes/container/websiteBuilderMarquee";

export function websiteBuilderSetStyleDeclaration(el: HTMLElement, styleDeclaration: string) {
  if (styleDeclaration?.length > 0) {
    el.style.cssText = styleDeclaration;
  }
}

function setDataset(el: HTMLElement, dataset: DOMStringMap) {
  Object.keys(dataset).forEach(key => {
    const value = dataset[key];

    if (value != null) {
      el.dataset[key] = value;
    }
  });
}

function setBooleanAttribute(el: HTMLElement, name: string, value: boolean) {
  if (value) {
    el.setAttribute(name, "");
  }
}

function appendSectionBackgroundDarken(section: HTMLElement, background: IWebsiteBuilderSectionBackground) {
  if (!background.darken) {
    return;
  }

  const darken = document.createElement("div");

  darken.className = WEBSITE_BUILDER_SECTION_BACKGROUND_DARKEN_CLASS_NAME;
  darken.style.backgroundImage = `linear-gradient(0deg, rgba(0, 0, 0, ${background.darkenPercent / 100}))`;
  darken.setAttribute("aria-hidden", "true");

  section.appendChild(darken);
}

function appendSectionBackgroundMedia(
  section: HTMLElement,
  background: IWebsiteBuilderSectionBackground,
  preview: boolean,
) {
  const media = document.createElement("video");

  media.className = WEBSITE_BUILDER_SECTION_BACKGROUND_MEDIA_CLASS_NAME;
  media.setAttribute("aria-hidden", "true");
  media.style.objectFit = "cover";
  media.style.objectPosition = `${background.xPercent}% ${background.yPercent}%`;

  setBooleanAttribute(media, "muted", true);
  setBooleanAttribute(media, "defaultMuted", true);
  setBooleanAttribute(media, "autoplay", true);
  setBooleanAttribute(media, "loop", true);
  setBooleanAttribute(media, "playsinline", true);
  setBooleanAttribute(media, "disablePictureInPicture", true);
  media.defaultMuted = true;
  media.disablePictureInPicture = true;

  const videoDataset: TWebsiteBuilderVideoDataset = {
    src: background.src,
  };

  setDataset(media, videoDataset);

  if (preview) {
    media.src = background.src;
  }

  section.appendChild(media);
  appendSectionBackgroundDarken(section, background);
}

function appendSectionParallaxMedia(section: HTMLElement, background: IWebsiteBuilderSectionBackground) {
  const media = document.createElement("img");

  media.className = WEBSITE_BUILDER_SECTION_PARALLAX_MEDIA_CLASS_NAME;
  media.alt = "";
  media.draggable = false;
  media.setAttribute("aria-hidden", "true");
  media.src = background.src;
  const parallaxShift = websiteBuilderGenerateSectionParallaxShift(background.parallaxAmount);

  media.style.objectPosition = `${background.xPercent}% ${background.yPercent}%`;
  media.style.setProperty(WB_SECTION_PARALLAX_SHIFT_CSS_VARIABLE, parallaxShift.shift);
  media.style.setProperty(WB_SECTION_PARALLAX_TRAVEL_CSS_VARIABLE, parallaxShift.travel);

  section.appendChild(media);
  appendSectionBackgroundDarken(section, background);
}

function setSectionLayout(el: HTMLElement, node: TWebsiteBuilderSection) {
  const layout = node.section.layout;
  
  el.style.setProperty(WB_SECTION_HEIGHT_DESKTOP_CSS_VARIABLE, `${layout.desktop.height}px`);
  el.style.setProperty(WB_SECTION_HEIGHT_TABLET_CSS_VARIABLE, `${layout.tablet.height}px`);
  el.style.setProperty(WB_SECTION_HEIGHT_MOBILE_CSS_VARIABLE, `${layout.mobile.height}px`);
}

function setSectionEntityLayout(el: HTMLElement, node: TWebsiteBuilderSectionEntity, parentNode: TWebsiteBuilderNode) {
  type TLayoutValue = {
    amount: number;
    unit: "px" | "%";
  }

  type TLayoutPadding = {
    vertical: TLayoutValue;
    horizontal: TLayoutValue;
  }

  type TLayoutConfig = {
    left: TLayoutValue;
    top: TLayoutValue;
    width: TLayoutValue;
    height: TLayoutValue;
    padding: TLayoutPadding;
  }

  type TLayoutConfigByDevice = {
    desktop: TLayoutConfig;
    tablet: TLayoutConfig;
    mobile: TLayoutConfig;
  }

  let layout: TWebsiteBuilderSectionEntityLayout = null;
  let stretch: IWebsiteBuilderSectionEntityStretch = null;

  if (node.type === "element") {
    layout = node.element.layout;
    stretch = isWebsiteBuilderStretchElementData(node.element)
      ? node.element.stretch
      : null;
  } else {
    layout = node.container.layout;
    stretch = isWebsiteBuilderStretchContainerData(node.container)
      ? node.container.stretch
      : null;
  }

  const configByDevice: TLayoutConfigByDevice = {
    desktop: {
      left: { amount: layout.desktop.position.x, unit: "px" },
      top: { amount: layout.desktop.position.y, unit: "px" },
      width: { amount: layout.desktop.dimensions.width, unit: "px" },
      height: { amount: layout.desktop.dimensions.height, unit: "px" },
      padding: null,
    },
    tablet: {
      left: { amount: layout.tablet.position.x, unit: "px" },
      top: { amount: layout.tablet.position.y, unit: "px" },
      width: { amount: layout.tablet.dimensions.width, unit: "px" },
      height: { amount: layout.tablet.dimensions.height, unit: "px" },
      padding: null,
    },
    mobile: {
      left: { amount: layout.mobile.position.x, unit: "px" },
      top: { amount: layout.mobile.position.y, unit: "px" },
      width: { amount: layout.mobile.dimensions.width, unit: "px" },
      height: { amount: layout.mobile.dimensions.height, unit: "px" },
      padding: null,
    },
  };

  const setAllDeviceConfigs = <K extends keyof TLayoutConfig>(
    key: K,
    value: TLayoutConfig[K],
  ) => {
    configByDevice.desktop[key] = cloneDeep(value);
    configByDevice.tablet[key] = cloneDeep(value);
    configByDevice.mobile[key] = cloneDeep(value);
  };

  if (
    node.type === "element" &&
    isWebsiteBuilderElementFlowParentNode(parentNode)
  ) {
    setAllDeviceConfigs("left", { amount: 0, unit: "px" });
    setAllDeviceConfigs("top", { amount: 0, unit: "px" });
  }

  if (parentNode.type === "section" && stretch?.enabled) {
    setAllDeviceConfigs("left", { amount: 0, unit: "px" });
    setAllDeviceConfigs("width", { amount: 100, unit: "%" });
    setAllDeviceConfigs("padding", { vertical: { amount: 0, unit: "px" }, horizontal: { amount: stretch.margin, unit: "px" }});
  }

  const setConfigStyleProperties = (config: TLayoutConfig, device: TWebsiteBuilderDevice) => {
    const setLayoutValueProperty = (key: string, layoutValue: TLayoutValue) => {
      if (layoutValue != null) {
        el.style.setProperty(key, `${layoutValue.amount}${layoutValue.unit}`);
      }
    };

    const setLayoutPaddingProperty = (key: string, layoutPadding: TLayoutPadding) => {
      if (layoutPadding != null) {
        el.style.setProperty(key, `${layoutPadding.vertical.amount}${layoutPadding.vertical.unit} ${layoutPadding.horizontal.amount}${layoutPadding.horizontal.unit}`);
      }
    };

    setLayoutValueProperty(websiteBuilderGetSectionEntityCssVariable(node.type, "left", device), config.left);
    setLayoutValueProperty(websiteBuilderGetSectionEntityCssVariable(node.type, "top", device), config.top);
    setLayoutValueProperty(websiteBuilderGetSectionEntityCssVariable(node.type, "width", device), config.width);
    setLayoutValueProperty(websiteBuilderGetSectionEntityCssVariable(node.type, "height", device), config.height);
    setLayoutPaddingProperty(websiteBuilderGetSectionEntityCssVariable(node.type, "padding", device), config.padding);
  };

  setConfigStyleProperties(configByDevice.desktop, "desktop");
  setConfigStyleProperties(configByDevice.tablet, "tablet");
  setConfigStyleProperties(configByDevice.mobile, "mobile");
}

const createPageAnchor = (
  item: IWebsiteBuilderWebsiteVersionNavigationMenuPageItem,
  pages: TWebsiteBuilderWebsiteVersionPage[],
  className: string,
  preview: boolean,
): HTMLAnchorElement => {
  const page = pages.find(page => page.id === item.page_id);
  
  const a = document.createElement("a");

  a.className = className;
  a.textContent = page?.title ?? "";
  a.href = page == null || page.is_home ? "/" : page.slug;
  a.target = "_self";

  if (preview) {
    a.dataset.previewPageId = String(item.page_id);
  }

  return a;
};

const createExternalAnchor = (
  item: IWebsiteBuilderWebsiteVersionNavigationMenuExternalItem,
  className: string,
): HTMLAnchorElement => {
  const a = document.createElement("a");
  
  a.className = className;
  a.textContent = item.label;
  a.href = websiteBuilderAddLinkProtocol(item.external_url);
  a.target = item.open_in_new_tab ? "_blank" : "_self";

  return a;
};

function setLinkDestination(a: HTMLAnchorElement, destination: TWebsiteBuilderLinkDestination) {
  if (destination.type === "url" && destination.href?.length > 0) {
    const { href, blankTarget } = destination;

    a.href = websiteBuilderAddLinkProtocol(href);
    a.target = blankTarget ? "_blank" : "_self";
  } else if (destination.type === "email" && destination.address?.length > 0) {
    const { subject, address } = destination;

    const params = new URLSearchParams();

    if (subject?.length > 0) {
      params.set("subject", subject);
    }

    const query = params.toString();
    a.href = `mailto:${address}${query.length > 0 ? `?${query}` : ""}`;
  }
}

export function websiteBuilderGeneratePageRootFragment(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  logoUrl: string,
  rootCssVariables: TWebsiteBuilderRootCSSVariables,
  navigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[],
  pages: TWebsiteBuilderWebsiteVersionPage[],
  activePageId: number,
  forms: IFormBuilderForm[],
  iconAssets: IWebsiteBuilderIconAsset[],
  preview: boolean,
): DocumentFragment {
  if (header == null || root == null || footer == null) {
    return new DocumentFragment();
  }
  
  function traverse(node: TWebsiteBuilderNode, parentNode: TWebsiteBuilderNode, grandParentNode: TWebsiteBuilderNode): HTMLElement {
    if (node.type === "root") {
      const root = document.createElement("div");

      root.id = "website-root";
      root.className = WEBSITE_BUILDER_ROOT_CLASS_NAME;

      Object.entries(rootCssVariables).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });

      root.appendChild(traverse(header, node, parentNode));

      const main = document.createElement("main");

      main.className = "website-main";

      node.children.forEach(child => {
        main.appendChild(traverse(child, node, parentNode));
      });

      root.appendChild(main);

      root.appendChild(traverse(footer, node, parentNode));

      return root;
    } else if (node.type === "section") {
      let section: HTMLElement = document.createElement("div");

      const sectionType = node.section.type;

      const isHeader = sectionType === "header";
      const isContent = sectionType === "content";
      const isFooter = sectionType === "footer";

      if (isHeader) {
        section = document.createElement("header");
      } else if (isContent) {
        section = document.createElement("section");
      } else if (isFooter) {
        section = document.createElement("footer");
      } else {
        return section;
      }

      const usesBackgroundVideo = isWebsiteBuilderSectionBackgroundVideo(node.section.background);
      const usesParallax = isWebsiteBuilderSectionParallax(node);
      const designStyles = websiteBuilderGenerateSectionDesignStyles(node.section.background);

      const visibility = websiteBuilderGetSectionVisibility(node, root);

      section.className = classNames(designStyles.className, "website-section", {
        [WEBSITE_BUILDER_SECTION_PARALLAX_CLASS_NAME]: usesParallax,
        "website-hide-node-desktop": !visibility.desktop,
        "website-hide-node-tablet": !visibility.tablet,
        "website-hide-node-mobile": !visibility.mobile,
      });
      
      websiteBuilderSetStyleDeclaration(section, designStyles.styleDeclaration);
      setSectionLayout(section, node);

      const backgroundImages: string[] = [];

      if (isHeader || isFooter) {
        backgroundImages.push(
          `linear-gradient(${isHeader ? "to top" : "to bottom"},
            ${WEBSITE_BUILDER_SECTION_BORDER_COLOR} 1px,
            transparent 1px
          )`,
        );
      }

      if (node.section.background.darken && !usesBackgroundVideo && !usesParallax) {
        backgroundImages.push(`linear-gradient(0deg, rgba(0, 0, 0, ${node.section.background.darkenPercent / 100}))`);
      }

      if (node.section.background.src && !usesBackgroundVideo && !usesParallax) {
        backgroundImages.push(`url(${node.section.background.src})`);
      }

      section.style.backgroundImage = backgroundImages.join(", ");

      if (usesBackgroundVideo) {
        appendSectionBackgroundMedia(section, node.section.background, preview);
      } else if (usesParallax) {
        appendSectionParallaxMedia(section, node.section.background);
      }

      const sectionBounded = document.createElement("div");

      sectionBounded.className = "website-section-bounded";

      node.children.forEach(child => {
        if (
          child.type === "element" &&
          isWebsiteBuilderStretchElementData(child.element) &&
          child.element.stretch.enabled
        ) {
          section.appendChild(traverse(child, node, parentNode));
        } else if (
          child.type === "container" &&
          isWebsiteBuilderStretchContainerData(child.container) &&
          child.container.stretch.enabled
        ) {
          section.appendChild(traverse(child, node, parentNode));
        } else {
          sectionBounded.appendChild(traverse(child, node, parentNode));
        }
      });

      section.appendChild(sectionBounded);

      return section;
    } else if (node.type === "container") {
      const container = document.createElement("div");

      container.className = classNames(WEBSITE_BUILDER_CONTAINER_CLASS_NAME, "website-container", {
        "website-hide-node-desktop": !node.container.visibility.desktop,
        "website-hide-node-tablet": !node.container.visibility.tablet,
        "website-hide-node-mobile": !node.container.visibility.mobile,
      });

      container.style.zIndex = String(node.container.zIndex);
      
      setSectionEntityLayout(container, node, parentNode);

      let content: HTMLElement = document.createElement("div");

      if (isWebsiteBuilderBox(node)) {
        const box = document.createElement("div");

        const settings = node.container.settings;
        const designStyles = websiteBuilderGenerateBoxDesignStyles(settings);

        box.className = classNames(designStyles.className, "website-box");
        websiteBuilderSetStyleDeclaration(box, designStyles.styleDeclaration);

        node.children.forEach(child => {
          box.appendChild(traverse(child, node, parentNode));
        });

        content = box;
      } else if (node.container.type === "flex") {
        const flex = document.createElement("div");

        const settings = node.container.settings;
        const designStyles = websiteBuilderGenerateFlexDesignStyles(settings);

        flex.className = designStyles.className;
        websiteBuilderSetStyleDeclaration(flex, designStyles.styleDeclaration);

        node.children.forEach(child => {
          flex.appendChild(traverse(child, node, parentNode));
        });

        content = flex;
      } else if (node.container.type === "grid") {
        const gridContainer = document.createElement("div");
        const grid = document.createElement("div");

        const settings = node.container.settings;
        const designStyles = websiteBuilderGenerateGridDesignStyles(settings);

        gridContainer.className = designStyles.containerClassName;
        grid.className = designStyles.className;
        websiteBuilderSetStyleDeclaration(grid, designStyles.styleDeclaration);

        node.children.forEach(child => {
          grid.appendChild(traverse(child, node, parentNode));
        });

        gridContainer.appendChild(grid);
        content = gridContainer;
      } else if (node.container.type === "marquee") {
        const marquee = document.createElement("div");
        const track = document.createElement("div");

        const settings = node.container.settings;
        const designStyles = websiteBuilderGenerateMarqueeDesignStyles(settings);

        marquee.className = classNames(designStyles.className, "website-marquee");
        websiteBuilderSetStyleDeclaration(marquee, designStyles.styleDeclaration);

        track.className = designStyles.trackClassName;

        settings.items.forEach((item, index) => {
          let itemElement: HTMLElement;

          if (item.type === "icon") {
            const itemDesignStyles = websiteBuilderGenerateMarqueeIconItemDesignStyles(item.settings);
            const iconAsset = websiteBuilderFindIconAsset(iconAssets, item.settings.assetIdentity);

            itemElement = document.createElement("div");
            itemElement.className = itemDesignStyles.className;
            websiteBuilderSetStyleDeclaration(itemElement, itemDesignStyles.styleDeclaration);
            itemElement.innerHTML = iconAsset?.svg ?? "";
          } else if (item.type === "link") {
            const itemDesignStyles = websiteBuilderGenerateMarqueeLinkItemDesignStyles(item.settings);

            const a = document.createElement("a");

            a.className = itemDesignStyles.className;
            websiteBuilderSetStyleDeclaration(a, itemDesignStyles.styleDeclaration);
            
            a.textContent = item.settings.label;

            setLinkDestination(a, item.settings.destination);

            itemElement = a;
          } else {
            const itemDesignStyles = websiteBuilderGenerateMarqueeParagraphItemDesignStyles(item.settings);

            itemElement = document.createElement("p");
            itemElement.className = itemDesignStyles.className;
            websiteBuilderSetStyleDeclaration(itemElement, itemDesignStyles.styleDeclaration);
            itemElement.textContent = item.settings.text;
          }

          track.append(itemElement);

          if (index < settings.items.length - 1) {
            const separator = document.createElement("div");

            separator.className = designStyles.separatorClassName;
            websiteBuilderSetStyleDeclaration(separator, designStyles.separatorStyleDeclaration);
            separator.setAttribute("aria-hidden", "true");
            track.append(separator);
          }
        });

        marquee.append(track);

        content = marquee;
      } else if (node.container.type === "slideshow") {
        const slideshow = document.createElement("div");
        const slideshowContent = document.createElement("div");
        const slideshowSlideTrack = document.createElement("div");

        const settings = node.container.settings;
        const designStyles = websiteBuilderGenerateSlideshowDesignStyles(settings);

        slideshow.className = classNames(designStyles.className, "website-slideshow", {
          "website-slideshow-expandable": settings.expandImageOnClick,
        });

        websiteBuilderSetStyleDeclaration(slideshow, designStyles.styleDeclaration);

        const slideshowDataset: TWebsiteBuilderSlideshowDataset = {
          autoplay: String(settings.autoplay),
          timePerSlide: String(settings.timePerSlide),
          activeSlideIndex: "0",
          transitioning: "false",
        };

        setDataset(slideshow, slideshowDataset);

        slideshowContent.className = designStyles.contentClassName;
        slideshowSlideTrack.className = designStyles.slideTrackClassName;

        const images = settings.images;

        if (images.length > 0) {
          images.forEach(image => {
            const slide = document.createElement("div");
            const backgroundImg = document.createElement("img");
            const img = document.createElement("img");

            slide.className = designStyles.slideClassName;
            backgroundImg.className = designStyles.backgroundImageClassName;
            img.className = designStyles.imageClassName;

            backgroundImg.src = image.src;
            backgroundImg.alt = "";
            backgroundImg.draggable = false;
            backgroundImg.ariaHidden = "true";

            img.src = image.src;
            img.alt = image.alt;
            img.draggable = false;

            slide.append(backgroundImg, img);
            slideshowSlideTrack.append(slide);
          });

          slideshowContent.append(slideshowSlideTrack);

          if (settings.showArrows && images.length > 1) {
            const leftChevron = document.createElement("button");
            const rightChevron = document.createElement("button");

            leftChevron.type = "button";
            rightChevron.type = "button";

            leftChevron.setAttribute("autocomplete", "off");
            rightChevron.setAttribute("autocomplete", "off");

            leftChevron.className = designStyles.leftChevronClassName;
            rightChevron.className = designStyles.rightChevronClassName;

            slideshowContent.appendChild(leftChevron);
            slideshowContent.appendChild(rightChevron);
          }

          if (settings.showPagination && images.length > 1) {
            const pagination = document.createElement("div");

            pagination.className = designStyles.paginationClassName;

            images.forEach((_, index) => {
              const dot = document.createElement("button");

              dot.type = "button";

              dot.className = classNames(designStyles.dotClassName, {
                [designStyles.dotActiveClassName]: index === 0,
              });

              pagination.appendChild(dot);
            });

            slideshowContent.appendChild(pagination);
          }

          slideshow.appendChild(slideshowContent);
        }

        content = slideshow;
      } else if (node.container.type === "form") {
        const formId = node.container.settings.formId;

        const form = formId == null
          ? null
          : forms.find(form => form.id === formId);

        if (form != null) {
          content = websiteBuilderGenerateForm(form);
        }
      } else if (node.container.type === "table") {
        const tableContainer = document.createElement("div");
        const table = document.createElement("table");
        
        const settings = node.container.settings;
        const tableDesignStyles = websiteBuilderGenerateTableDesignStyles();
        
        tableContainer.className = tableDesignStyles.containerClassName;
        table.className = tableDesignStyles.className;
        
        const thead = document.createElement("thead");
        const theadRow = document.createElement("tr");

        const headRowDesignStyles = websiteBuilderGenerateTableRowDesignStyles(settings.head.row);

        websiteBuilderSetStyleDeclaration(theadRow, headRowDesignStyles.styleDeclaration);

        for (const cell of settings.head.row.cells) {
          const th = document.createElement("th");
          const content = document.createElement("div");

          const cellDesignStyles = websiteBuilderGenerateTableCellDesignStyles(cell);

          if (cell.value.type === "icon") {
            content.className = `${cellDesignStyles.contentClassName} ${cellDesignStyles.iconClassName}`;
            websiteBuilderSetStyleDeclaration(content, cellDesignStyles.iconStyleDeclaration);

            const iconAsset = websiteBuilderFindIconAsset(iconAssets, cell.value.assetIdentity);

            content.innerHTML = iconAsset?.svg ?? "";
          } else {
            content.className = cellDesignStyles.contentClassName;
            content.textContent = cell.value.content;
          }

          th.appendChild(content);
          theadRow.appendChild(th);
        }

        thead.appendChild(theadRow);

        const tbody = document.createElement("tbody");

        for (const row of settings.body.rows) {
          const tr = document.createElement("tr");

          const bodyRowDesignStyles = websiteBuilderGenerateTableRowDesignStyles(row);

          websiteBuilderSetStyleDeclaration(tr, bodyRowDesignStyles.styleDeclaration);

          for (let i = 0; i < row.cells.length; i++) {
            const cell = row.cells[i];

            const td = document.createElement("td");
            const content = document.createElement("div");

            const mobileContentContainer = document.createElement("div");
            const mobileContent = document.createElement("div");

            const mobileContentCell = settings.head.row.cells[i];
            const mobileContentCellDesignStyles = websiteBuilderGenerateTableCellDesignStyles(mobileContentCell);
            
            mobileContentContainer.className = mobileContentCellDesignStyles.mobileContentContainerClassName;

            if (mobileContentCell.value.type === "icon") {
              mobileContent.className = `${mobileContentCellDesignStyles.contentClassName} ${mobileContentCellDesignStyles.iconClassName}`;
              websiteBuilderSetStyleDeclaration(mobileContent, mobileContentCellDesignStyles.iconStyleDeclaration);

              const iconAsset = websiteBuilderFindIconAsset(iconAssets, mobileContentCell.value.assetIdentity);

              mobileContent.innerHTML = iconAsset?.svg ?? "";
            } else {
              mobileContent.className = mobileContentCellDesignStyles.contentClassName;
              mobileContent.textContent = mobileContentCell.value.content;
            }

            const cellDesignStyles = websiteBuilderGenerateTableCellDesignStyles(cell);

            if (cell.value.type === "icon") {
              content.className = `${cellDesignStyles.contentClassName} ${cellDesignStyles.iconClassName}`;
              websiteBuilderSetStyleDeclaration(content, cellDesignStyles.iconStyleDeclaration);

              const iconAsset = websiteBuilderFindIconAsset(iconAssets, cell.value.assetIdentity);

              content.innerHTML = iconAsset?.svg ?? "";
            } else {
              content.className = cellDesignStyles.contentClassName;
              content.textContent = cell.value.content;
            }

            mobileContentContainer.appendChild(mobileContent);
            td.appendChild(mobileContentContainer);
            td.appendChild(content);
            tr.appendChild(td);
          }

          tbody.appendChild(tr);
        }

        table.appendChild(thead);
        table.appendChild(tbody);
        tableContainer.appendChild(table);
        content = tableContainer;
      } else if (isWebsiteBuilderTabset(node)) {
        const tabset = document.createElement("div");

        const settings = node.container.settings;
        const designStyles = websiteBuilderGenerateTabsetDesignStyles();

        tabset.className = classNames(designStyles.className, "website-tabset");
        websiteBuilderSetStyleDeclaration(tabset, designStyles.styleDeclaration);

        const leftChevron = document.createElement("button");
        const rightChevron = document.createElement("button");

        leftChevron.type = "button";
        rightChevron.type = "button";

        leftChevron.style.height = `${WEBSITE_BUILDER_TABSET_HEADER_HEIGHT}px`;
        rightChevron.style.height = `${WEBSITE_BUILDER_TABSET_HEADER_HEIGHT}px`;

        leftChevron.className = "website-tabset-left-chevron";
        rightChevron.className = "website-tabset-right-chevron";

        tabset.append(leftChevron, rightChevron);

        const tabHeader = document.createElement("div");

        tabHeader.className = designStyles.tabHeaderClassName;
        websiteBuilderSetStyleDeclaration(tabHeader, designStyles.tabHeaderStyleDeclaration);

        node.children.forEach(tab => {
          const tabHeaderItem = document.createElement("div");

          tabHeaderItem.className = classNames(designStyles.tabHeaderItemClassName, {
            [designStyles.tabHeaderItemActiveClassName]: tab.id === settings.defaultTabId,
          });

          tabHeaderItem.textContent = tab.settings.title;

          tabHeader.appendChild(tabHeaderItem);
        });

        tabset.appendChild(tabHeader);

        node.children.forEach(tab => {
          tabset.appendChild(traverse(tab, node, parentNode));
        });

        content = tabset;
      }

      container.appendChild(content);

      return container;
    } else if (node.type === "grid-cell") {
      const gridCell = document.createElement("div");

      const designStyles = websiteBuilderGenerateGridCellDesignStyles();

      gridCell.className = designStyles.className;

      websiteBuilderSetStyleDeclaration(gridCell, designStyles.styleDeclaration);

      if (websiteBuilderGridCellFilled(node)) {
        gridCell.appendChild(traverse(node.children[0], node, parentNode));
      }

      return gridCell;
    } else if (node.type === "tab" && isWebsiteBuilderTabset(parentNode)) {
      const tab = document.createElement("div");

      const designStyles = websiteBuilderGenerateTabDesignStyles();

      tab.className = classNames(designStyles.className, "website-tab", {
        "website-tab-active": node.id === parentNode.container.settings.defaultTabId,
      });

      websiteBuilderSetStyleDeclaration(tab, designStyles.styleDeclaration);

      node.children.forEach(child => {
        tab.appendChild(traverse(child, node, parentNode));
      });

      return tab;
    } else if (node.type === "element" && isWebsiteBuilderElementParentNode(parentNode)) {
      const element = document.createElement("div");

      const designStyles = websiteBuilderGenerateElementDesignStyles(parentNode);
      const elementFilled = websiteBuilderElementFilled(false, node, parentNode, grandParentNode);

      element.className = classNames(designStyles.className, "website-element", {
        "website-hide-node-desktop": !node.element.visibility.desktop,
        "website-hide-node-tablet": !node.element.visibility.tablet,
        "website-hide-node-mobile": !node.element.visibility.mobile,
      });

      websiteBuilderSetStyleDeclaration(element, designStyles.styleDeclaration);

      element.style.zIndex = isWebsiteBuilderElementFlowParentNode(parentNode) ? "0" : String(node.element.zIndex);

      if (node.element.type === "navigation-menu") {
        // Navigation menu dropdowns need to take priority. Can maybe be replaced with a portal.
        const NAV_Z_INDEX_OVERRIDE = 999999;

        element.style.zIndex = String(NAV_Z_INDEX_OVERRIDE);
      }

      setSectionEntityLayout(element, node, parentNode);

      if (elementFilled) {
        element.style.width = "100%";
        element.style.height = "100%";
      }

      let content: HTMLElement = document.createElement("div");

      if (node.element.type === "button") {
        const button = document.createElement("button");

        button.type = "button";

        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateButtonDesignStyles(settings);

        button.className = `${designStyles.className} website-button`;
        websiteBuilderSetStyleDeclaration(button, designStyles.styleDeclaration);

        const label = document.createElement("div");

        label.className = designStyles.labelClassName;
        label.textContent = settings.label;

        const iconAsset = websiteBuilderFindIconAsset(iconAssets, settings.icon?.assetIdentity);

        const icon = document.createElement("div");

        if (iconAsset != null) {
          icon.className = designStyles.iconClassName;
          icon.innerHTML = iconAsset.svg;
        }

        const buttonContent = document.createElement("div");

        buttonContent.className = designStyles.contentClassName;

        if (settings.icon?.position === "left" && iconAsset != null) {
          buttonContent.appendChild(icon);
        }

        buttonContent.appendChild(label);

        if (settings.icon?.position === "right" && iconAsset != null) {
          buttonContent.appendChild(icon);
        }

        button.appendChild(buttonContent);

        const buttonDataset: TWebsiteBuilderButtonDataset = {
          linkType: settings.link.type,
          linkHref: settings.link.type === "url" && settings.link.href?.length > 0 ? websiteBuilderAddLinkProtocol(settings.link.href) : undefined,
          linkBlankTarget: settings.link.type === "url" && settings.link.blankTarget != null ? String(settings.link.blankTarget) : undefined,
          linkAddress: settings.link.type === "email" && settings.link.address?.length > 0 ? settings.link.address : undefined,
          linkSubject: settings.link.type === "email" && settings.link.subject?.length > 0 ? settings.link.subject : undefined,
        };

        setDataset(button, buttonDataset);

        content = button;
      } else if (node.element.type === "divider") {
        const hr = document.createElement("hr");

        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateDividerDesignStyles(settings);

        hr.className = designStyles.className;
        websiteBuilderSetStyleDeclaration(hr, designStyles.styleDeclaration);

        content = hr;
      } else if (node.element.type === "embed") {
        const container = document.createElement("div");

        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateEmbedDesignStyles();

        container.className = designStyles.containerClassName;

        const iframeSettings = websiteBuilderGenerateEmbedIframeSettings(settings);

        if (iframeSettings != null) {
          const iframe = document.createElement("iframe");

          iframe.className = designStyles.className;
          websiteBuilderSetStyleDeclaration(iframe, designStyles.styleDeclaration);

          iframe.src = iframeSettings.src;
          iframe.loading = iframeSettings.loading;
          iframe.title = iframeSettings.title;
          iframe.referrerPolicy = iframeSettings.referrerPolicy;

          if (iframeSettings.allowFullScreen != null) {
            iframe.allowFullscreen = iframeSettings.allowFullScreen;
          }

          if (iframe.allow != null) {
            iframe.allow = iframeSettings.allow;
          }

          container.appendChild(iframe);
        }

        content = container;
      } else if (node.element.type === "heading") {
        const settings = node.element.settings;

        const template = document.createElement("template");
        template.innerHTML = websiteBuilderRenderRichTextToHTML({
          extensions: websiteBuilderHeadingRichTextExtensions,
          content: settings.content,
        });

        const renderedElement = template.content.firstElementChild;

        if (renderedElement instanceof HTMLElement) {
          content = renderedElement;
        }
      } else if (node.element.type === "icon") {
        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateIconDesignStyles(settings);

        const iconAsset = websiteBuilderFindIconAsset(iconAssets, settings.assetIdentity);

        let container: HTMLElement;

        if (settings.link.type === "none") {
          container = document.createElement("div");
        } else {
          const a = document.createElement("a");

          setLinkDestination(a, settings.link);

          if (iconAsset != null) {
            a.setAttribute("aria-label", iconAsset.title);
          }

          container = a;
        }

        container.className = classNames(designStyles.containerClassName, {
          "website-icon-link": settings.link.type !== "none",
        });

        if (iconAsset != null) {
          const icon = document.createElement("div");

          icon.className = designStyles.className;
          websiteBuilderSetStyleDeclaration(icon, designStyles.styleDeclaration);

          icon.innerHTML = iconAsset.svg;

          container.appendChild(icon);
        }

        content = container;
      } else if (node.element.type === "image") {
        const container = document.createElement("div");
        
        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateImageDesignStyles(settings);
        
        container.className = classNames(designStyles.containerClassName, "website-image-container");
        websiteBuilderSetStyleDeclaration(container, designStyles.containerStyleDeclaration);

        if (settings.src) {
          const img = document.createElement("img");

          img.className = classNames(designStyles.className, "website-image", {
            "website-image-expandable": settings.expandOnClick,
          });
          websiteBuilderSetStyleDeclaration(img, designStyles.styleDeclaration);

          img.src = settings.src;
          img.alt = settings.alt;

          container.appendChild(img);
        }

        content = container;
      } else if (node.element.type === "video") {
        const container = document.createElement("div");

        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateVideoDesignStyles(settings);

        container.className = designStyles.containerClassName;

        if (settings.src) {
          const video = document.createElement("video");

          video.className = designStyles.className;
          websiteBuilderSetStyleDeclaration(video, designStyles.styleDeclaration);

          setBooleanAttribute(video, "controls", true);
          setBooleanAttribute(video, "playsinline", true);

          setBooleanAttribute(video, "muted", settings.muted);
          setBooleanAttribute(video, "defaultMuted", settings.muted);
          setBooleanAttribute(video, "autoplay", settings.autoPlay);
          setBooleanAttribute(video, "loop", settings.loop);
          
          if (settings.description?.length > 0) {
            video.setAttribute("aria-label", settings.description);
          }

          const videoDataset: TWebsiteBuilderVideoDataset = {
            src: settings.src,
          };

          setDataset(video, videoDataset);

          if (preview) {
            video.src = settings.src;
          }

          container.appendChild(video);
        }

        content = container;
      } else if (node.element.type === "link") {
        const a = document.createElement("a");

        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateLinkDesignStyles(settings);

        a.className = `${designStyles.className} website-link`;
        websiteBuilderSetStyleDeclaration(a, designStyles.styleDeclaration);

        a.textContent = settings.label;

        setLinkDestination(a, settings.destination);

        content = a;
      } else if (node.element.type === "list") {
        const settings = node.element.settings;
        const list = document.createElement(settings.type === "ordered" ? "ol" : "ul");
        
        const designStyles = websiteBuilderGenerateListDesignStyles();

        list.className = designStyles.className;
        websiteBuilderSetStyleDeclaration(list, designStyles.styleDeclaration);

        settings.items.forEach(item => {
          const li = document.createElement("li");

          li.textContent = item;
          list.appendChild(li);
        });

        content = list;
      } else if (node.element.type === "logo") {
        const container = document.createElement("div");
        
        const settings = node.element.settings;
        const designStyles = websiteBuilderGenerateLogoDesignStyles(settings, logoUrl);
        
        container.className = designStyles.containerClassName;
  
        if (logoUrl) {
          const a = document.createElement("a");

          a.className = "website-logo-link";

          a.href = "/";
          a.target = "_self";

          const img = document.createElement("img");
          
          img.className = `${designStyles.className} website-logo`;
          websiteBuilderSetStyleDeclaration(img, designStyles.styleDeclaration);
  
          img.src = logoUrl;
          img.alt = settings.alt;

          a.appendChild(img);
  
          container.appendChild(a);
        }
  
        content = container;
      } else if (node.element.type === "navigation-menu") {
        const nav = document.createElement("nav");

        const designStyles = websiteBuilderGenerateNavigationMenuDesignStyles();

        nav.className = `${designStyles.className} website-navigation-menu`;
        websiteBuilderSetStyleDeclaration(nav, designStyles.styleDeclaration);

        const getAnchorClassName = (pageId?: number) => classNames(designStyles.linkClassName, "website-navigation-menu-link", {
          [designStyles.linkActiveClassName]: pageId === activePageId,
        });

        for (const item of navigationMenuItems) {
          if (item.hidden) {
            continue;
          }
          
          if (item.type === "page") {
            nav.appendChild(createPageAnchor(item, pages, getAnchorClassName(item.page_id), preview));
          } else if (item.type === "external") {
            nav.appendChild(createExternalAnchor(item, getAnchorClassName()));
          } else if (item.type === "dropdown") {
            const dropdown = document.createElement("div");
            const dropbutton = document.createElement("div");
            const dropdownContent = document.createElement("div");

            dropdown.className = "website-navigation-menu-dropdown";
            dropbutton.className = designStyles.dropbuttonClassName;
            dropdownContent.className = "website-navigation-menu-dropdown-content";

            dropbutton.textContent = `${item.label} ▼`;

            for (const childItem of item.children) {
              if (childItem.hidden) {
                continue;
              }

              if (childItem.type === "page") {
                dropdownContent.appendChild(createPageAnchor(childItem, pages, getAnchorClassName(childItem.page_id), preview));
              } else if (childItem.type === "external") {
                dropdownContent.appendChild(createExternalAnchor(childItem, getAnchorClassName()));
              }
            }

            dropdown.appendChild(dropbutton);
            dropdown.appendChild(dropdownContent);

            nav.appendChild(dropdown);
          }
        }

        content = nav;
      } else if (node.element.type === "paragraph") {
        const settings = node.element.settings;

        const template = document.createElement("template");
        template.innerHTML = websiteBuilderRenderRichTextToHTML({
          extensions: websiteBuilderParagraphRichTextExtensions,
          content: settings.content,
        });

        const renderedElement = template.content.firstElementChild;

        if (renderedElement instanceof HTMLElement) {
          content = renderedElement;
        }
      }

      element.appendChild(content);

      return element;
    } else {
      return document.createElement("div");
    }
  }

  const rootElement = traverse(root, null, null);

  const documentFragment = new DocumentFragment();

  documentFragment.appendChild(rootElement);

  return documentFragment;
}

function websiteBuilderGenerateImageModal() {
  const modal = document.createElement("div");
  modal.id = "website-image-modal";

  const content = document.createElement("div");
  content.className = "website-image-modal-content";

  const imageContainer = document.createElement("div");
  imageContainer.id = "website-image-modal-image-container";

  const xIcon = document.createElement("div");
  xIcon.id = "website-image-modal-x-icon";

  content.append(imageContainer, xIcon);
  modal.append(content);

  return modal;
}

function websiteBuilderGenerateSlideshowModel() {
  const modal = document.createElement("div");
  modal.id = "website-slideshow-modal";

  const content = document.createElement("div");
  content.className = "website-slideshow-modal-content";

  const slideTrack = document.createElement("div");
  slideTrack.id = "website-slideshow-modal-slide-track";

  const xIcon = document.createElement("div");
  xIcon.id = "website-slideshow-modal-x-icon";

  const leftChevron = document.createElement("button");
  leftChevron.id = "website-slideshow-modal-left-chevron";
  leftChevron.type = "button";
  leftChevron.hidden = true;

  const rightChevron = document.createElement("button");
  rightChevron.id = "website-slideshow-modal-right-chevron";
  rightChevron.type = "button";
  rightChevron.hidden = true;

  const pagination = document.createElement("div");
  pagination.id = "website-slideshow-modal-pagination";
  pagination.hidden = true;

  content.append(slideTrack, xIcon, leftChevron, rightChevron, pagination);
  modal.append(content);

  return modal;
}

function websiteBuilderGeneratePagePopoverElement(
  navigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[],
  pages: TWebsiteBuilderWebsiteVersionPage[],
  activePageId: number,
) {
  const popover = document.createElement("div");
  popover.id = "website-popover";

  const xIcon = document.createElement("div");
  xIcon.id = "website-popover-x-icon";

  popover.appendChild(xIcon);

  const popoverContent = document.createElement("div");
  popoverContent.className = "website-popover-content";

  const getAnchorClassName = (pageId?: number) => classNames("website-popover-link", {
    "website-popover-link-active": pageId === activePageId,
  });

  for (const item of navigationMenuItems) {
    if (item.hidden) {
      continue;
    }

    if (item.type === "page") {
      popoverContent.appendChild(createPageAnchor(item, pages, getAnchorClassName(item.page_id), false));
    } else if (item.type === "external") {
      popoverContent.appendChild(createExternalAnchor(item, getAnchorClassName()));
    } else if (item.type === "dropdown") {
      const dropdown = document.createElement("div");
      const dropbutton = document.createElement("div");
      const dropdownContent = document.createElement("div");

      dropdown.className = "website-popover-dropdown";
      dropbutton.className = "website-popover-dropdown-dropbutton";
      dropdownContent.className = "website-popover-dropdown-content";

      dropbutton.textContent = `${item.label}`;

      for (const childItem of item.children) {
        if (childItem.hidden) {
          continue;
        }

        if (childItem.type === "page") {
          dropdownContent.appendChild(createPageAnchor(childItem, pages, getAnchorClassName(childItem.page_id), false));
        } else if (childItem.type === "external") {
          dropdownContent.appendChild(createExternalAnchor(childItem, getAnchorClassName()));
        }
      }

      dropdown.appendChild(dropbutton);
      dropdown.appendChild(dropdownContent);

      popoverContent.appendChild(dropdown);
    }
  }

  popover.appendChild(popoverContent);

  return popover;
}

export function websiteBuilderGeneratePageFragment(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  title: string,
  cssFilename: string,
  scriptFilename: string,
  faviconUrl: string,
  logoUrl: string,
  rootCssVariables: TWebsiteBuilderRootCSSVariables,
  pages: TWebsiteBuilderWebsiteVersionPage[],
  activePageId: number,
  navigationMenuItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[],
  forms: IFormBuilderForm[],
  iconAssets: IWebsiteBuilderIconAsset[],
  t: TFunction,
): DocumentFragment {
  const html = document.createElement("html");
  html.id = "website-html";
  html.lang = "en";

  const head = document.createElement("head");

  const charset = document.createElement("meta");
  charset.setAttribute("charset", "UTF-8");
  head.appendChild(charset);

  const viewport = document.createElement("meta");
  viewport.name = "viewport";
  // Prevents browser zoom-out from counteracting the website root's CSS scale
  viewport.content = "width=device-width, initial-scale=1.0, minimum-scale=1.0";
  head.appendChild(viewport);

  const favicon = document.createElement("link");
  favicon.rel = "icon";
  favicon.href = faviconUrl;
  head.appendChild(favicon);

  const stylesheet = document.createElement("link");
  stylesheet.rel = "stylesheet";
  stylesheet.href = `css/${cssFilename}`;
  head.appendChild(stylesheet);

  const titleEl = document.createElement("title");
  titleEl.textContent = title ?? t("Untitled Page");
  head.appendChild(titleEl);

  const websiteConfig: IWebsiteConfig = {
    adminBaseUrl: process.env.BASE_URL,
  };

  const configScript = document.createElement("script");

  configScript.textContent =
    `window.${WINDOW_WEBSITE_CONFIG} = ` +
    JSON.stringify(websiteConfig) +
    ";";

  head.appendChild(configScript);

  const body = document.createElement("body");
  body.id = "website-body";
  
  const rootFragment = websiteBuilderGeneratePageRootFragment(header, root, footer, logoUrl, rootCssVariables, navigationMenuItems, pages, activePageId, forms, iconAssets, false);
  body.appendChild(rootFragment);

  const imageModal = websiteBuilderGenerateImageModal();
  body.appendChild(imageModal);

  const slideshowModal = websiteBuilderGenerateSlideshowModel();
  body.appendChild(slideshowModal);

  const popoverElement = websiteBuilderGeneratePagePopoverElement(navigationMenuItems, pages, activePageId);
  body.appendChild(popoverElement);

  const toastContainerElement = document.createElement("div");
  toastContainerElement.id = "website-toast-container";

  body.appendChild(toastContainerElement);

  const script = document.createElement("script");
  script.src = "js/" + scriptFilename;
  body.appendChild(script);

  html.appendChild(head);
  html.appendChild(body);

  const documentFragment = new DocumentFragment();

  documentFragment.appendChild(html);

  return documentFragment;
}
